import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Typography } from '@mui/material';

export interface InfoDialogProps extends DialogProps {
  message: string;
  title: string;
  onDismiss: () => void;
  onReadLater: () => void;
}

export const InfoDialog: React.FC<InfoDialogProps> = ({ open, message, title, onClose, onDismiss, onReadLater }) => {
  const imageOnlyMessage = /^\s*<p>\s*<img\b[^>]*\/?>\s*<\/p>\s*$/i.test(message.trim());

  // Resize images in the message to fit the dialog width dynamically, for retina displays.
  let resizedMessage = message.replace(/<img\s+src="([^"]+)"([^>]*)>/g, (_, url, rest) => {
    // Should be the window width minus 112px
    // 32px margin on each side of the modal
    // 24px padding on the inside of each side of the modal
    // Should have a max of 600px (max width of modal) -48px (24px padding on each side) for regular messages
    // Should have a max of 600px (max width of modal) for image-only messages
    const displayWidth = imageOnlyMessage
      ? Math.min(window.innerWidth - 64, 600)
      : Math.min(window.innerWidth - 112, 552);
    const retinaWidth = displayWidth * 2;

    const sep = url.includes('?') ? '&' : '?';

    return `<img src="${url}${sep}w=${retinaWidth}" style="width:${displayWidth}px; height:auto;"${rest}>`;
  });

  // If it's an image-only message, add inline style to the <p> to remove padding/margin
  if (imageOnlyMessage) {
    resizedMessage = resizedMessage.replace(/<p>/i, '<p style="padding:0; margin:0;">');
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true}>
      {/* Hide the title if the message is only an image */}
      {!imageOnlyMessage && <DialogTitle>{title}</DialogTitle>}
      {/* Remove the padding if the message is only an image */}
      <DialogContent sx={imageOnlyMessage ? { p: 0 } : undefined}>
        <Typography component={'div'} dangerouslySetInnerHTML={{ __html: resizedMessage }}></Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onReadLater}>I&apos;ll read it later</Button>
        <Button variant={'contained'} onClick={onDismiss}>
          Got It!
        </Button>
      </DialogActions>
    </Dialog>
  );
};
